import { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Fade,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TableSortLabel
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Info from './Info';
import LanguageSelector from './LanguageSelector';
import { useAdminLanguage } from './AdminI18n';
import useTableSort from './useTableSort';

function RecipientAdmin() {
  const { t } = useAdminLanguage();
  const [recipients, setRecipients] = useState([]);
  const [formData, setFormData] = useState({ nom: '' });
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [language, setLanguage] = useState('fr');

  useEffect(() => {
    fetchRecipients();
  }, [language]);

  const fetchRecipients = async () => {
    try {
      const response = await fetch(`/api/recipients?lang=${language}`);
      const data = await response.json();
      setRecipients(data);
      setLoading(false);
    } catch (err) {
      setError('Erreur lors du chargement des destinataires');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      if (!formData.nom.trim()) {
        setError('Le nom est obligatoire');
        return;
      }

      setLoading(true);
      setError('');
      setSuccess('');

      if (isEditing) {
        await fetch(`/api/recipients/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...formData, lang: language }),
        });
        setSuccess('Destinataire mis à jour avec succès');
      } else {
        await fetch('/api/recipients', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...formData, lang: language }),
        });
        setSuccess('Destinataire ajouté avec succès');
      }

      setFormData({ nom: '' });
      setIsEditing(false);
      setEditingId(null);
      fetchRecipients();
    } catch (err) {
      setError('Erreur lors de l\'enregistrement du destinataire');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (recipient) => {
    setIsEditing(true);
    setEditingId(recipient.id);
    setFormData({ nom: recipient.nom });
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await fetch(`/api/recipients/${id}?lang=${language}`, {
        method: 'DELETE',
      });
      setSuccess('Destinataire supprimé avec succès');
      fetchRecipients();
    } catch (err) {
      setError('Erreur lors de la suppression du destinataire');
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(() => ([
    { id: 'nom', label: t("Nom"), sortable: true },
  ]), [t]);

  const { order, orderBy, sortedRows, handleRequestSort } = useTableSort(recipients, {
    defaultOrderBy: 'nom',
  });

  const renderCellContent = (recipient, columnId) => recipient[columnId];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Fade in timeout={500}>
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            align="center"
            color="primary"
            sx={{ fontWeight: 'bold', mb: 4 }}
          >
            {t('Gestion des destinataires')}
          </Typography>

          <Info type="recipient" />

          <LanguageSelector 
            language={language} 
            onLanguageChange={setLanguage} 
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t(error)}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t(success)}
            </Alert>
          )}

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              {t(isEditing ? 'Modifier le destinataire' : 'Ajouter un destinataire')}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="nom"
                  label={t("Nom")}
                  autoComplete="off"
                  value={formData.nom}
                  onChange={handleInputChange}
                  variant="outlined"
                  required
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={loading}
                  startIcon={loading && <CircularProgress size={20} />}
                >
                  {loading ? t('Enregistrement...') : t(isEditing ? 'Modifier' : 'Ajouter')}
                </Button>
                {isEditing && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      setIsEditing(false);
                      setEditingId(null);
                      setFormData({ nom: '' });
                    }}
                    sx={{ ml: 2 }}
                  >
                    {t('Annuler')}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom>
              {t('Liste des destinataires')}
            </Typography>

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        sortDirection={orderBy === column.id ? order : false}
                      >
                        {column.sortable ? (
                          <TableSortLabel
                            active={orderBy === column.id}
                            direction={orderBy === column.id ? order : 'asc'}
                            onClick={() => handleRequestSort(column.id)}
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    ))}
                    <TableCell>{t('Actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedRows.map((recipient) => (
                    <TableRow key={recipient.id}>
                      {columns.map((column) => (
                        <TableCell key={`${recipient.id}-${column.id}`}>
                          {renderCellContent(recipient, column.id)}
                        </TableCell>
                      ))}
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(recipient)}
                            color="primary"
                            disabled={loading}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(recipient.id)}
                            color="error"
                            disabled={loading}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Fade>
    </Container>
  );
}

export default RecipientAdmin;










